import { mergeMarkdownPreviewStyle, type MarkdownPreviewStyle } from "../settings/markdownPreviewStyle.js";

export function openMarkdownPreviewStyleDialog(opts: {
  initial: MarkdownPreviewStyle;
  onSave: (next: MarkdownPreviewStyle) => void;
}): void {
  const dlg = document.createElement("dialog");
  dlg.className = "npp-dialog npp-dialog-markdown-style";
  dlg.innerHTML = `
    <form method="dialog" class="npp-dialog-form">
      <h2 class="npp-dialog-title">Markdown preview style</h2>
      <fieldset>
        <legend>Layout & body</legend>
        <label class="npp-dialog-row">Line breaks
          <select name="lineBreaks" class="npp-dialog-input npp-dialog-select">
            <option value="commonmark">CommonMark (blank line between paragraphs)</option>
            <option value="gfm">GitHub-style (single newline → line break)</option>
          </select>
        </label>
        <label class="npp-dialog-row">Body text size
          <select name="fontScale" class="npp-dialog-input npp-dialog-select">
            <option value="compact">Compact</option>
            <option value="normal">Normal</option>
            <option value="comfortable">Comfortable</option>
          </select>
        </label>
        <label class="npp-dialog-row">Column width
          <select name="contentWidth" class="npp-dialog-input npp-dialog-select">
            <option value="full">Full width</option>
            <option value="readable">Readable (~40rem)</option>
            <option value="article">Article (~50rem)</option>
          </select>
        </label>
        <label class="npp-dialog-row">Body typeface
          <select name="proseFont" class="npp-dialog-input npp-dialog-select">
            <option value="sans">System sans-serif</option>
            <option value="serif">Serif</option>
          </select>
        </label>
        <label class="npp-dialog-row">Paragraph spacing
          <select name="paragraphSpacing" class="npp-dialog-input npp-dialog-select">
            <option value="compact">Tight</option>
            <option value="normal">Normal</option>
            <option value="relaxed">Relaxed</option>
          </select>
        </label>
        <label class="npp-dialog-row">List indent & spacing
          <select name="listDensity" class="npp-dialog-input npp-dialog-select">
            <option value="compact">Compact</option>
            <option value="normal">Normal</option>
            <option value="relaxed">Relaxed</option>
          </select>
        </label>
      </fieldset>
      <fieldset>
        <legend>Headings (h1–h6)</legend>
        <label class="npp-dialog-row">Size scale (all levels, relative to body)
          <select name="headingScale" class="npp-dialog-input npp-dialog-select">
            <option value="compact">Compact</option>
            <option value="balanced">Balanced (default)</option>
            <option value="editorial">Editorial (larger titles)</option>
            <option value="prominent">Prominent</option>
          </select>
        </label>
        <label class="npp-dialog-row">Heading typeface
          <select name="headingFont" class="npp-dialog-input npp-dialog-select">
            <option value="inherit">Same as body</option>
            <option value="sans">Sans-serif</option>
            <option value="serif">Serif</option>
          </select>
        </label>
      </fieldset>
      <fieldset>
        <legend>Code</legend>
        <label class="npp-dialog-row">Monospace font
          <select name="codeFont" class="npp-dialog-input npp-dialog-select">
            <option value="menlo">Menlo / Consolas stack</option>
            <option value="fira">Fira Code / Fira Mono</option>
            <option value="jetbrains">JetBrains Mono</option>
            <option value="source">Source Code Pro</option>
          </select>
        </label>
        <label class="npp-dialog-row">Code size (inline + blocks)
          <select name="codeSize" class="npp-dialog-input npp-dialog-select">
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </label>
        <label class="npp-dialog-row">Fenced block background
          <select name="codeBlockTone" class="npp-dialog-input npp-dialog-select">
            <option value="muted">Muted gray (default)</option>
            <option value="plain">Plain white</option>
            <option value="high-contrast">Higher contrast</option>
          </select>
        </label>
      </fieldset>
      <fieldset>
        <legend>Quotes, tables, links</legend>
        <label class="npp-dialog-row">Blockquote emphasis
          <select name="blockquoteTone" class="npp-dialog-input npp-dialog-select">
            <option value="subtle">Subtle</option>
            <option value="normal">Normal</option>
            <option value="strong">Strong</option>
          </select>
        </label>
        <label class="npp-dialog-row">Table borders
          <select name="tableStyle" class="npp-dialog-input npp-dialog-select">
            <option value="bordered">Full grid</option>
            <option value="minimal">Horizontal rules only</option>
            <option value="striped">Striped rows</option>
          </select>
        </label>
        <label class="npp-dialog-row">Table cell padding
          <select name="tableDensity" class="npp-dialog-input npp-dialog-select">
            <option value="compact">Compact</option>
            <option value="normal">Normal</option>
            <option value="comfortable">Comfortable</option>
          </select>
        </label>
        <label class="npp-dialog-row">Link style
          <select name="linkTone" class="npp-dialog-input npp-dialog-select">
            <option value="blue">GitHub blue</option>
            <option value="accent">Body color + underline</option>
            <option value="neutral-underline">Muted + underline</option>
          </select>
        </label>
      </fieldset>
      <p class="npp-dialog-hint">Applies to the Markdown preview pane and PDF export from preview. Stored in this browser.</p>
      <div class="npp-dialog-actions">
        <button type="submit" value="ok">OK</button>
        <button type="submit" value="cancel">Cancel</button>
      </div>
    </form>`;
  document.body.append(dlg);
  const q = <K extends keyof MarkdownPreviewStyle>(name: K) =>
    dlg.querySelector<HTMLSelectElement>(`select[name="${String(name)}"]`)!;

  const fields: (keyof MarkdownPreviewStyle)[] = [
    "lineBreaks",
    "fontScale",
    "contentWidth",
    "proseFont",
    "paragraphSpacing",
    "listDensity",
    "headingScale",
    "headingFont",
    "codeFont",
    "codeSize",
    "codeBlockTone",
    "blockquoteTone",
    "tableStyle",
    "tableDensity",
    "linkTone",
  ];
  for (const name of fields) {
    q(name).value = String(opts.initial[name]);
  }

  const readSelect = (name: keyof MarkdownPreviewStyle): string => q(name).value;

  const form = dlg.querySelector("form")!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const sub = (e as SubmitEvent).submitter as HTMLButtonElement | null;
    if (!sub || sub.value !== "ok") {
      dlg.close();
      return;
    }
    const raw: Record<string, string> = {};
    for (const name of fields) {
      raw[name] = readSelect(name);
    }
    opts.onSave(mergeMarkdownPreviewStyle(raw));
    dlg.close();
  });
  dlg.addEventListener("close", () => dlg.remove());
  dlg.showModal();
  q("lineBreaks").focus();
}
